import { readRuntimeSettings } from './environment.runtime';
import { RuntimeHostSettings, RuntimeOrganization, RuntimeSelection, RuntimeUser } from './environment.types';

type EnvironmentShape = {
    production: boolean;
    settings: {
        url: {
            api: string;
            web: string;
            mobile: string;
        };
        app: {
            name: string;
            code: string;
        };
        support: {
            email: string;
            mobile: string;
            fullname: string;
        };
    };
    theme: {
        logo: string;
        theme: string;
        watermark: string;
        favicon: string;
    };
    userState: {
        profile: RuntimeUser;
        roles: {
            super: boolean;
            admin: boolean;
            manager: boolean;
            user: boolean;
        };
        access: Record<string, any>;
        organizations: RuntimeOrganization[];
    };
    selectionState: {
        organization: any;
        branch: any;
        indexes: RuntimeSelection;
    };
    url: {
        api: string;
        web: string;
        mobile: string;
    };
    app: {
        name: string;
        code: string;
    };
    support: {
        email: string;
        mobile: string;
        fullname: string;
    };
    layout: {
        logo: string;
        theme: string;
        watermark: string;
        favicon: string;
    };
    user: RuntimeUser;
    roles: {
        super: boolean;
        admin: boolean;
        manager: boolean;
        user: boolean;
    };
    access: Record<string, any>;
    organizations: RuntimeOrganization[];
    selection: {
        organization: number;
        branch: number;
        organizationData: any;
        branchData: any;
    };
};

const FALLBACKS = {
    url: {
        api: 'https://api.ashcorp.xyz',
        web: 'https://demo.ashcorp.xyz',
        mobile: 'https://mobile.ashcorp.co'
    },
    app: {
        name: 'DEMO',
        code: 'DEMO'
    },
    support: {
        email: 'support@ashcorp.co',
        mobile: '59729663',
        fullname: 'Addams'
    },
    theme: {
        logo: 'https://cloud.ashcorp.co/index.php/s/fX7kZWEkEysJrqb/download/logo-20260328151452-e9f33243.png',
        theme: 'default',
        watermark: 'https://cloud.ashcorp.co/index.php/s/Btp2mpragCTSniC/download/watermark-20260328151320-0bcf126c.png',
        favicon: 'https://cloud.ashcorp.co/index.php/s/MwMofkqidT9pYPZ/download/favicon-20260328151518-4af7f78e.png'
    },
    user: {
        avatar: 'https://cloud.ashcorp.co/index.php/s/g7ewbkDSAY6eTC3/download/avatar-20260328152021-746da634.jpeg'
    }
} as const;

function firstNonEmptyString(...values: Array<unknown>): string | null {
    for (const value of values) {
        if (typeof value !== 'string') {
            continue;
        }

        const trimmed = value.trim();
        if (trimmed.length > 0) {
            return trimmed;
        }
    }

    return null;
}

function normalizeApiUrl(value: string): string {
    return value.replace(/\/+$/, '');
}

function toFiniteNumber(value: unknown, fallback: number): number {
    return Number.isFinite(value as number) ? Number(value) : fallback;
}

export function createEnvironment(production: boolean): EnvironmentShape {
    const settings: RuntimeHostSettings = readRuntimeSettings();

    const settingsNamespace = {
        url: {
            api: normalizeApiUrl(firstNonEmptyString(
                settings.settings?.url?.api,
                settings.url?.api,
                FALLBACKS.url.api
            ) as string),
            web: firstNonEmptyString(
                settings.settings?.url?.web,
                settings.url?.web,
                FALLBACKS.url.web
            ) as string,
            mobile: firstNonEmptyString(
                settings.settings?.url?.mobile,
                settings.url?.mobile,
                FALLBACKS.url.mobile
            ) as string
        },
        app: {
            name: firstNonEmptyString(
                settings.settings?.app?.name,
                settings.app?.name,
                FALLBACKS.app.name
            ) as string,
            code: firstNonEmptyString(
                settings.settings?.app?.code,
                settings.app?.code,
                FALLBACKS.app.code
            ) as string
        },
        support: {
            email: firstNonEmptyString(
                settings.settings?.support?.email,
                settings.support?.email,
                FALLBACKS.support.email
            ) as string,
            mobile: firstNonEmptyString(
                settings.settings?.support?.mobile,
                settings.support?.mobile,
                FALLBACKS.support.mobile
            ) as string,
            fullname: firstNonEmptyString(
                settings.settings?.support?.fullname,
                settings.support?.fullname,
                FALLBACKS.support.fullname
            ) as string
        }
    };

    const themeNamespace = {
        logo: firstNonEmptyString(settings.theme?.logo, settings.layout?.logo, FALLBACKS.theme.logo) as string,
        theme: firstNonEmptyString(settings.theme?.theme, settings.layout?.theme, FALLBACKS.theme.theme) as string,
        watermark: firstNonEmptyString(settings.theme?.watermark, settings.layout?.watermark, FALLBACKS.theme.watermark) as string,
        favicon: firstNonEmptyString(settings.theme?.favicon, settings.layout?.favicon, FALLBACKS.theme.favicon) as string
    };

    const userNamespace = {
        profile: {
            email: firstNonEmptyString(settings.userState?.profile?.email, settings.user?.email) || '',
            fullname: firstNonEmptyString(settings.userState?.profile?.fullname, settings.user?.fullname) || '',
            lastname: firstNonEmptyString(settings.userState?.profile?.lastname, settings.user?.lastname) || '',
            othernames: firstNonEmptyString(settings.userState?.profile?.othernames, settings.user?.othernames) || '',
            roles: settings.userState?.profile?.roles || settings.user?.roles || [],
            superadmin: settings.userState?.profile?.superadmin === true || settings.user?.superadmin === true,
            avatar: firstNonEmptyString(
                settings.userState?.profile?.avatar,
                settings.user?.avatar,
                settings.app?.avatar,
                FALLBACKS.user.avatar
            ) as string
        },
        roles: {
            super: settings.userState?.roles?.super === true || settings.roles?.super === true,
            admin: settings.userState?.roles?.admin === true || settings.roles?.admin === true,
            manager: settings.userState?.roles?.manager === true || settings.roles?.manager === true,
            user: settings.userState?.roles?.user === true || settings.roles?.user === true
        },
        access: settings.userState?.access || settings.access || {} as Record<string, any>,
        organizations: settings.userState?.organizations || settings.organizations || []
    };

    const selectionNamespace = {
        organization: settings.selectionState?.organization || (settings as any).organization || null,
        branch: settings.selectionState?.branch || (settings as any).branch || null,
        indexes: {
            organization: toFiniteNumber(
                settings.selectionState?.indexes?.organization,
                toFiniteNumber(settings.selection?.organization, -1)
            ),
            branch: toFiniteNumber(
                settings.selectionState?.indexes?.branch,
                toFiniteNumber(settings.selection?.branch, -1)
            )
        }
    };

    return {
        production,
        settings: settingsNamespace,
        theme: themeNamespace,
        userState: userNamespace,
        selectionState: selectionNamespace,
        url: settingsNamespace.url,
        app: settingsNamespace.app,
        support: settingsNamespace.support,
        layout: themeNamespace,
        user: userNamespace.profile,
        roles: userNamespace.roles,
        access: userNamespace.access,
        organizations: userNamespace.organizations,
        selection: {
            organization: selectionNamespace.indexes.organization,
            branch: selectionNamespace.indexes.branch,
            organizationData: selectionNamespace.organization,
            branchData: selectionNamespace.branch
        }
    };
}